"use client";
import React, { useState, useEffect } from 'react';

const Overclock = () => {
  const [clicks, setClicks] = useState(0);
  const [timeLeft, setTimeLeft] = useState(10);
  const [isPlaying, setIsPlaying] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [highScore, setHighScore] = useState(0);

  useEffect(() => {
    let timer;
    if (isPlaying && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && isPlaying) {
      setIsPlaying(false);
      setGameOver(true);
      if (clicks > highScore) setHighScore(clicks);
    }
    return () => clearInterval(timer);
  }, [isPlaying, timeLeft, clicks, highScore]);

  const handleClick = () => {
    if (!isPlaying && !gameOver) {
      setIsPlaying(true);
    }
    if (isPlaying) {
      setClicks(prev => prev + 1);
    }
  };

  const resetGame = () => {
    setClicks(0);
    setTimeLeft(10);
    setIsPlaying(false);
    setGameOver(false);
  };

  // Calculate "temperature" color based on clicks, using shades of grey/black
  const getTempColor = () => {
    if (clicks < 20) return 'text-brand-muted border-gray-300 shadow-sm';
    if (clicks < 40) return 'text-brand-muted border-gray-500 shadow-md';
    if (clicks < 60) return 'text-brand-text border-gray-700 shadow-lg';
    return 'text-brand-text border-gray-900 shadow-xl animate-pulse';
  };

  const getCPS = () => {
    if (timeLeft === 10) return 0;
    const elapsed = 10 - timeLeft;
    return (clicks / elapsed).toFixed(1);
  };

  return (
    <div className="w-full bg-brand-surface border border-gray-300 rounded-lg p-6 text-center shadow-sm relative overflow-hidden flex flex-col items-center justify-center min-h-[350px]">
      
      {!gameOver ? (
        <>
          <h2 className="text-xl font-black uppercase tracking-widest text-brand-text mb-2">Overclock CPU</h2>
          <p className="text-brand-muted text-sm mb-8">Click the core as fast as possible for 10 seconds.</p>

          <div className="flex w-full justify-between px-4 mb-6">
            <div className="text-left">
              <span className="block text-xs text-brand-muted uppercase tracking-wider">Time</span>
              <span className="text-2xl font-bold text-brand-text">{timeLeft}s</span>
            </div>
            <div className="text-right">
              <span className="block text-xs text-brand-muted uppercase tracking-wider">Speed</span>
              <span className="text-2xl font-bold text-brand-text">{getCPS()} <span className="text-sm">GHz</span></span>
            </div>
          </div>

          <button 
            onClick={handleClick}
            className={`w-32 h-32 rounded-full border-4 flex items-center justify-center bg-brand-surface-alt hover:bg-[var(--brand-surface-hover)] transition-all active:scale-95 ${getTempColor()}`}
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <div className="text-center pointer-events-none">
              <span className="block text-3xl font-black">{clicks}</span>
              <span className="block text-xs uppercase tracking-widest opacity-70">Clicks</span>
            </div>
          </button>
          
          {!isPlaying && clicks === 0 && (
             <div className="mt-6 text-sm text-brand-text animate-pulse uppercase tracking-widest font-bold">Tap to Start</div>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center h-full w-full">
          <h2 className="text-3xl font-black text-brand-text mb-2 uppercase tracking-widest">Thermal Throttled!</h2>
          <div className="text-gray-700 mb-6 flex flex-col gap-2">
            <div className="text-lg">Final Speed: <span className="text-brand-text font-bold">{clicks} clicks</span></div>
            <div className="text-sm">Average: <span className="text-brand-text font-bold">{(clicks/10).toFixed(1)} CPS</span></div>
            {highScore > 0 && <div className="text-xs text-brand-muted mt-2">Personal Best: {highScore}</div>}
          </div>
          
          <button 
            onClick={resetGame}
            className="px-8 py-3 bg-brand-secondary text-brand-text-inverse font-bold uppercase tracking-widest rounded-full hover:bg-[var(--brand-surface-hover)] transition-colors shadow-sm"
          >
            Cool Down & Retry
          </button>
        </div>
      )}
    </div>
  );
};

export default Overclock;
